/**
 * An assessment's paper: which questions, in what order and section, for how
 * many marks.
 *
 * The paper stores placements, not questions. Each placement points at a bank
 * question by id and carries only what belongs to the paper (its number,
 * section, marks, and the measured results from earlier sittings). Wording,
 * type, topic, difficulty, Bloom's level and status are read from the bank when
 * the paper is read, so editing a question anywhere changes it on every paper
 * that uses it, and a paper can never hold a stale copy.
 */

import type { LivePaperQuestion } from "@/lib/assessment-live-paper"
import { bankQuestionToLivePaper } from "@/lib/assessment-live-paper"
import { hasLibraryBankRowOverlay } from "@/lib/library-bank-row-overlays"
import { bankRowById, ensureInBank } from "@/lib/question-bank/bank-store"
import { mergeOverlayIntoBankQuestion } from "@/lib/question-version-history/persist"
import { createPersistedStore } from "@/lib/persisted-store"

export type Placement = Pick<
  LivePaperQuestion,
  | "id"
  | "number"
  | "sectionId"
  | "points"
  | "pointsLocked"
  | "isNew"
  | "leoThinking"
  | "leoAct"
  | "leoActDetail"
  | "leoSource"
  | "difficultyPercentage"
  | "pointBiserial"
  | "discriminationIndex"
  | "usedBefore"
  | "usedCount"
  | "usedInSameCourse"
  | "aiSpotted"
  | "fixedSummary"
> & {
  /** Only used if the bank row is gone, so the paper still reads. */
  stemFallback: string
}

export const paperStore = createPersistedStore<Record<string, Placement[]>>(
  "shell:assessment-papers:v1",
  () => ({}),
)

export function placementOf(row: LivePaperQuestion): Placement {
  return {
    id: row.id,
    number: row.number,
    sectionId: row.sectionId,
    points: row.points,
    pointsLocked: row.pointsLocked,
    isNew: row.isNew,
    leoThinking: row.leoThinking,
    leoAct: row.leoAct,
    leoActDetail: row.leoActDetail,
    leoSource: row.leoSource,
    difficultyPercentage: row.difficultyPercentage,
    pointBiserial: row.pointBiserial,
    discriminationIndex: row.discriminationIndex,
    usedBefore: row.usedBefore,
    usedCount: row.usedCount,
    usedInSameCourse: row.usedInSameCourse,
    aiSpotted: row.aiSpotted,
    fixedSummary: row.fixedSummary,
    stemFallback: row.stem,
  }
}

/** The paper's questions as the page reads them: placements joined to the bank. */
export function hydratePlacement(placement: Placement): LivePaperQuestion {
  const base = bankRowById(placement.id)
  if (!base) {
    return {
      id: placement.id,
      number: placement.number,
      stem: placement.stemFallback,
      type: "Multiple Choice",
      topic: "Untagged",
      difficulty: "",
      difficultyPercentage: placement.difficultyPercentage,
      pointBiserial: placement.pointBiserial,
      discriminationIndex: placement.discriminationIndex,
      blooms: "",
      points: placement.points,
      usedBefore: placement.usedBefore,
      usedCount: placement.usedCount,
      usedInSameCourse: placement.usedInSameCourse,
      sectionId: placement.sectionId,
      // A slot Leo is still filling has no bank row yet; it must stay Leo's.
      pointsLocked: placement.pointsLocked,
      isNew: placement.isNew,
      leoThinking: placement.leoThinking,
      leoAct: placement.leoAct,
      leoActDetail: placement.leoActDetail,
      leoSource: placement.leoSource,
      aiSpotted: placement.aiSpotted,
      fixedSummary: placement.fixedSummary,
    }
  }
  const row = hasLibraryBankRowOverlay(base.id) ? mergeOverlayIntoBankQuestion(base) : base
  const fromBank = bankQuestionToLivePaper(row, {
    number: placement.number,
    sectionId: placement.sectionId ?? "",
  })
  return {
    ...fromBank,
    // An untagged difficulty reads as untagged on the paper too.
    difficulty: row.difficultyTagged === false ? "" : fromBank.difficulty,
    sectionId: placement.sectionId,
    points: placement.points,
    pointsLocked: placement.pointsLocked,
    isNew: placement.isNew ?? fromBank.isNew,
    leoThinking: placement.leoThinking,
    leoAct: placement.leoAct,
    leoActDetail: placement.leoActDetail,
    leoSource: placement.leoSource,
    difficultyPercentage: placement.difficultyPercentage,
    pointBiserial: placement.pointBiserial,
    discriminationIndex: placement.discriminationIndex,
    usedBefore: placement.usedBefore,
    usedCount: placement.usedCount,
    usedInSameCourse: placement.usedInSameCourse,
    aiSpotted: placement.aiSpotted ?? fromBank.aiSpotted,
    fixedSummary: placement.fixedSummary,
  }
}

export function readPaper(assessmentId: string): LivePaperQuestion[] | null {
  const placements = paperStore.get()[assessmentId]
  if (!placements) return null
  return placements.map(hydratePlacement)
}

/** Save the paper. Any question not yet in the bank is filed first. */
export function writePaper(assessmentId: string, rows: readonly LivePaperQuestion[]): void {
  ensureInBank(rows)
  paperStore.set((current) => ({ ...current, [assessmentId]: rows.map(placementOf) }))
}

export function clearPapers(): void {
  paperStore.set({})
}
